/**
 * Flat `filters` query shape used by several v2 list endpoints (similar intent to
 * MUI `GridFilterModel`, but with `field` / `operator` / `value` rows and `logic`).
 */
export type ApiV2FlatFilterItem = {
  field: string;
  operator: string;
  value: string;
};

export type ApiV2FlatFilterModel = {
  items: ApiV2FlatFilterItem[];
  logic: 'and' | 'or';
};

export function stringifyApiV2FlatFilterModel(
  model: ApiV2FlatFilterModel | null | undefined,
): string {
  if (!model?.items?.length) return '';
  return JSON.stringify(model);
}

/**
 * Unified “search anything” slice: one trimmed string applied with **`contains`**
 * across the given fields, combined with **`logic: 'or'`** between those rows.
 */
export function buildUnifiedSearchContainsFilterModel(
  searchText: string,
  fields: readonly string[],
): ApiV2FlatFilterModel | null {
  const value = searchText.trim();
  const validFields = fields.map(f => String(f).trim()).filter(Boolean);
  if (!value || !validFields.length) return null;
  return {
    items: validFields.map(field => ({
      field,
      operator: 'contains',
      value,
    })),
    logic: 'or',
  };
}

/**
 * Concatenates two models and sets top-level **`logic: 'and'`** so every row in
 * `a` must hold together with every row in `b`.
 *
 * Note: this does not preserve an internal **`or`** inside `a` or `b` once merged;
 * use only when `a` is effectively conjunctive, or when the API supports grouped
 * filters elsewhere.
 */
export function mergeApiV2FlatFilterModelsWithAnd(
  a: ApiV2FlatFilterModel | null | undefined,
  b: ApiV2FlatFilterModel | null | undefined,
): ApiV2FlatFilterModel | null {
  const itemsA = a?.items?.length ? a.items : [];
  const itemsB = b?.items?.length ? b.items : [];
  if (!itemsA.length && !itemsB.length) return null;
  if (!itemsA.length) return b ?? null;
  if (!itemsB.length) return a ?? null;
  return { items: [...itemsA, ...itemsB], logic: 'and' };
}
